import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Card,
    Divider,
    Empty,
    Form,
    Input,
    notification,
    Popconfirm,
    Segmented,
    Select,
    TreeSelect,
} from "antd";
import { CloseCircleFilled, PlusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { IAudienceDetails, IAudienceTagDetails } from "@Models/ILicenseDetails";
import LicenseService from "@Services/LicenseService";
import TokenisedInput from "@Components/shared/TokenisedInput";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import "@Styles/components/audiences.scss";
import LargeSpinner from "@Components/shared/LargeSpinner";
import { useActivityCategoriesTree } from "@Components/shared/hooks/useActivityCategoriesTree";
import { IActivityDefinitions } from "@Models/IActivityDetails";
import { QueryKeys } from "@Constants";
import { pluralise } from "@Utils";
import { useQueryClient } from "@tanstack/react-query";
import appQueryClient from "@AppQueryClient";
import GuidanceDrawer from "@Components/shared/GuidanceDrawer";
import { getTutorialTip } from "@Components/assessment/helpers";
import IconButton from "@Components/shared/IconButton";

const { Option } = Select;

interface ISegmentedOptions {
    label: string;
    value: string;
}

interface ICascaderOptions {
    label: string;
    value: string;
    children?: ICascaderOptions[];
}

const AudiencesTab: React.FC = () => {
    const [audiences, setAudiences] = useState<IAudienceDetails[]>([]);
    const [currentId, setCurrentId] = useState<string>();
    const [fetching, setFetching] = useState<boolean>(false);
    const [updating, setUpdating] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [product, setProduct] = useState<string>("");
    const [activities, setActivities] = useState<string[]>([]);
    const stateRef = useRef<IAudienceDetails>();
    const audienceOptions = audiences.map((audience) => ({ value: audience.audienceId, label: audience.name }));
    const definitions = appQueryClient.getQueryData<IActivityDefinitions>([QueryKeys.ActivityDefinitions]);
    const activityTree = useActivityCategoriesTree(product);
    const queryClient = useQueryClient();

    const productOptions: ISegmentedOptions[] = [];
    definitions?.products?.forEach((product) => {
        productOptions.push({
            value: product.productId,
            label: product.label,
        });
    });

    /**
     * Get all audiences.
     */
    const getAudiences = async () => {
        if (audiences.length === 0) {
            setFetching(true);
        }
        const licenseService = new LicenseService();
        const audiencesResult = await licenseService.getAudiences();
        if (audiences.length === 0) {
            setFetching(false);
        }
        if (!audiencesResult.hasErrors) {
            setAudiences(audiencesResult.value);
            if (audiencesResult.value.length) {
                if (audiences.length === 0) {
                    stateRef.current = audiencesResult.value[0];
                }
                setCurrentId(stateRef.current.audienceId);
                setProduct(stateRef.current.productId);
                setActivities(stateRef.current.activities.map((x) => x.id));
            }
        } else {
            notification.error({ message: "Error retrieving audiences." });
        }
    };

    /**
     * Create a new audience.
     */
    const createAudience = async () => {
        const newAudience: IAudienceDetails = {
            name: "New Audience",
            productId: definitions?.products[0]?.productId,
            description: "",
            activities: [],
            tags: [],
        };
        setUpdating(true);
        setCreating(true);
        const licenseService = new LicenseService();
        const createResult = await licenseService.createAudience(newAudience);
        setUpdating(false);
        setCreating(false);
        if (!createResult.hasErrors) {
            newAudience.audienceId = createResult.value.id;
            setAudiences([...audiences, newAudience]);
            stateRef.current = newAudience;
            setCurrentId(stateRef.current.audienceId);
            setProduct(stateRef.current.productId);
            setActivities([]);
            await queryClient.invalidateQueries([QueryKeys.AdminLicenses]);
            notification.success({ message: "Audience created." });
        } else {
            notification.error({ message: "Error creating audience." });
        }
    };

    /**
     * Delete current audience.
     */
    const deleteAudience = async () => {
        if (stateRef.current === null) return;
        setUpdating(true);
        setDeleting(true);
        const licenseService = new LicenseService();
        const deleteResult = await licenseService.deleteAudience(stateRef.current.audienceId);
        setUpdating(false);
        setDeleting(false);
        if (!deleteResult.hasErrors) {
            const newAudiences = audiences.filter((x) => x.audienceId !== stateRef.current.audienceId);
            setAudiences(newAudiences);
            const newCurrent = newAudiences.length ? audiences[0] : null;
            stateRef.current = newCurrent;
            setCurrentId(newCurrent?.audienceId);
            setProduct(newAudiences.length ? newCurrent?.productId : null);
            setActivities(newAudiences.length ? newCurrent?.activities.map((x) => x.id) : []);
            await queryClient.invalidateQueries([QueryKeys.AdminLicenses]);
            notification.success({ message: "Audience deleted." });
        } else {
            notification.error({ message: "Error deleting audience." });
        }
    };

    /**
     * Update current audience.
     */
    const updateAudience = async () => {
        if (stateRef.current === null) {
            return;
        }
        setUpdating(true);
        const licenseService = new LicenseService();
        const updateResult = await licenseService.updateAudience(stateRef.current);
        setUpdating(false);
        if (!updateResult.hasErrors) {
            notification.success({ message: "Audience updated." });
        } else {
            notification.error({ message: "Error updating audience." });
        }
    };

    /**
     * Trigger an update two seconds after last keypress.
     */
    const debouncePromise = useCallback(AwesomeDebouncePromise(updateAudience, 2000), []);

    /**
     * Select a new product and reset the activities selection.
     * @param val
     */
    const selectProduct = async (val) => {
        setProduct(val);
        setActivities([]);
        const newAudience = { ...stateRef.current };
        newAudience.productId = val;
        newAudience.activities = [];
        await triggerUpdate(newAudience);
    };

    /**
     * Modify the activity selection.
     * @param val
     */
    const changeActivities = async (val) => {
        setActivities(val);
        const newAudience = { ...stateRef.current };
        newAudience.activities = val.map((x) => ({ id: x }));
        await triggerUpdate(newAudience);
    };

    /**
     * Trigger an update to the form data state.
     * @param tagId
     * @param field
     * @param value
     */
    const onTagInputChange = async (tagId: string, field: string, value: string) => {
        const newAudience = { ...stateRef.current };
        const index = newAudience.tags.findIndex((x) => x.audienceTagId === tagId);
        if (index !== -1) {
            newAudience.tags[index][field] = value;
            await triggerUpdate(newAudience);
        }
    };

    /**
     * Trigger an update to the form data state.
     * @param field
     * @param value
     */
    const onAudienceInputChange = async (field: string, value: string) => {
        const newAudience = { ...stateRef.current };
        newAudience[field] = value;
        await triggerUpdate(newAudience);
    };

    /**
     * Update the audience state.
     * @param updatedAudience
     */
    const updateAudienceState = (updatedAudience: IAudienceDetails) => {
        const newAudiences = [...audiences];
        const index = newAudiences.findIndex((x) => x.audienceId === stateRef.current.audienceId);
        newAudiences[index] = updatedAudience;
        setAudiences(newAudiences);
    };

    /**
     * Update the audience state, the state ref and trigger the debounced promise to the service update call.
     * @param newAudience
     */
    const triggerUpdate = async (newAudience: IAudienceDetails) => {
        updateAudienceState(newAudience);
        stateRef.current = newAudience;
        await debouncePromise();
    };

    /**
     * Update audience metadataSchema
     * @param tagId
     * @param tags
     */
    const updateTagMetadata = async (tagId: string, tags: string[]) => {
        const newAudience = { ...stateRef.current };
        if (tags === null) tags = [];
        const index = newAudience.tags.findIndex((x) => x.audienceTagId === tagId);
        if (index !== -1) {
            newAudience.tags[index].options = tags;
            await triggerUpdate(newAudience);
        }
    };

    /**
     * Create a new tag.
     */
    const addTag = async () => {
        const newAudience = { ...stateRef.current };
        const newTag: IAudienceTagDetails = {
            audienceId: newAudience.audienceId,
            name: "new_tag",
            label: "New Tag",
            type: "text",
            options: [],
        };
        newAudience.tags.push(newTag);
        stateRef.current = newAudience;
        updateAudienceState(newAudience);
        await updateAudience();
        await getAudiences();
    };

    /**
     * Delete a tag.
     * @param tagId
     */
    const deleteTag = async (tagId: string) => {
        const newAudience = { ...stateRef.current };
        newAudience.tags = newAudience.tags.filter((x) => x.audienceTagId !== tagId);
        stateRef.current = newAudience;
        updateAudienceState(newAudience);
        await updateAudience();
    };

    /**
     * Select an audience
     * @param value
     */
    const onSelectAudience = (value) => {
        stateRef.current = audiences.find((x) => x.audienceId === value);
        setCurrentId(stateRef.current.audienceId);
        setProduct(stateRef.current.productId);
        setActivities(stateRef.current.activities.map((x) => x.id));
    };

    /**
     * Select an audience tag type.
     * @param tagId
     * @param value
     */
    const onSelectType = async (tagId: string, value: string) => {
        const newAudience = { ...stateRef.current };
        const index = newAudience.tags.findIndex((x) => x.audienceTagId === tagId);
        if (index !== -1) {
            newAudience.tags[index].type = value;
            if (value == "boolean") {
                newAudience.tags[index].options = ["Yes", "No"];
            } else {
                newAudience.tags[index].options = [];
            }
            stateRef.current = newAudience;
            updateAudienceState(newAudience);
            await updateAudience();
        }
    };

    useEffect(() => {
        getAudiences().then();
    }, []);

    const u = stateRef.current?.usages;
    return (
        <>
            <div className="guidance-container">
                <GuidanceDrawer text={getTutorialTip("audiences")} />
            </div>
            <Card className="audiences-tab newUI-raised">
                {fetching ? (
                    <LargeSpinner height={600} />
                ) : (
                    <div>
                        <Form className={"audience-edit-form"} initialValues={{}}>
                            <Form.Item label={"Select"}>
                                <div className={"audiences-top"}>
                                    <div>
                                        <Select
                                            className="audience-select"
                                            value={stateRef.current?.audienceId}
                                            loading={!audienceOptions.length}
                                            showSearch
                                            placeholder="Select an audience"
                                            optionFilterProp="children"
                                            onChange={onSelectAudience}
                                            filterOption={(input, option) =>
                                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={audienceOptions}
                                        />
                                    </div>
                                    <div>
                                        <IconButton
                                            icon={<PlusCircleOutlined />}
                                            disabled={updating}
                                            loading={creating}
                                            className="secondary"
                                            onClick={() => createAudience()}
                                        >
                                            &nbsp;New
                                        </IconButton>
                                    </div>
                                    <div>
                                        <Popconfirm
                                            disabled={updating}
                                            placement="leftBottom"
                                            title="Delete this audience?"
                                            onConfirm={() => deleteAudience()}
                                            cancelButtonProps={{ className: "secondary" }}
                                        >
                                            <IconButton
                                                disabled={updating}
                                                loading={deleting}
                                                className="secondary danger"
                                                icon={<CloseCircleFilled />}
                                            >
                                                &nbsp;Delete
                                            </IconButton>
                                        </Popconfirm>
                                    </div>
                                </div>
                                <Alert
                                    style={{ marginTop: "20px" }}
                                    description={
                                        <>
                                            There {pluralise("is", u)} {u?.toString() ?? "0"} {pluralise("license", u)}{" "}
                                            using this audience.
                                        </>
                                    }
                                    type={u > 0 ? "warning" : "info"}
                                    showIcon
                                />
                            </Form.Item>
                        </Form>

                        <Divider />
                        {audiences.length ? (
                            <Form className={"audience-edit-form"} initialValues={{}}>
                                <div className={"form-title"}>
                                    <h4>Details</h4>
                                    <hr />
                                </div>
                                <Form.Item label={"Name"}>
                                    <Input
                                        name={"name"}
                                        value={stateRef.current?.name}
                                        onChange={(e) => onAudienceInputChange(e.target.name, e.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label={"Description"}>
                                    <Input
                                        name={"description"}
                                        value={stateRef.current?.description}
                                        onChange={(e) => onAudienceInputChange(e.target.name, e.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label={"Product"}>
                                    <Segmented
                                        disabled={u > 0}
                                        value={product}
                                        block
                                        name={"productId"}
                                        options={productOptions}
                                        onChange={(val) => selectProduct(val)}
                                    />
                                </Form.Item>
                                <Form.Item label={"Activities"} className="activity-select">
                                    <TreeSelect
                                        placeholder="Select or type..."
                                        value={activities}
                                        treeData={activityTree}
                                        treeCheckable={true}
                                        onChange={changeActivities}
                                    />
                                </Form.Item>
                                <Divider />
                                <h4>Tags</h4>
                                <hr />
                                {stateRef.current?.tags.length > 0 && (
                                    <table className={"audience-tags-table"}>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Label</th>
                                                <th>Type</th>
                                                <th>Options</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stateRef.current?.tags.map((tag) => {
                                                return (
                                                    <tr>
                                                        <td>
                                                            <Form.Item>
                                                                <Input
                                                                    disabled={updating}
                                                                    name={"name"}
                                                                    placeholder="Name"
                                                                    value={tag.name}
                                                                    onChange={(e) =>
                                                                        onTagInputChange(
                                                                            tag.audienceTagId,
                                                                            e.target.name,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </td>
                                                        <td>
                                                            <Form.Item>
                                                                <Input
                                                                    disabled={updating}
                                                                    name={"label"}
                                                                    placeholder="Label"
                                                                    value={tag.label}
                                                                    onChange={(e) =>
                                                                        onTagInputChange(
                                                                            tag.audienceTagId,
                                                                            e.target.name,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </td>
                                                        <td>
                                                            <Form.Item>
                                                                <Select
                                                                    disabled={updating}
                                                                    placeholder="Type"
                                                                    value={tag.type}
                                                                    onChange={(value) =>
                                                                        onSelectType(tag.audienceTagId, value)
                                                                    }
                                                                >
                                                                    <Option value="text">Text</Option>
                                                                    <Option value="numeric">Numeric</Option>
                                                                    <Option value="boolean">Yes / No</Option>
                                                                    <Option value="option">Single</Option>
                                                                    <Option value="multiple">Multiple</Option>
                                                                </Select>
                                                            </Form.Item>
                                                        </td>
                                                        <td>
                                                            {(tag.type === "option" || tag.type === "multiple") && (
                                                                <Form.Item>
                                                                    <TokenisedInput
                                                                        maxOptions={10}
                                                                        handleOnChange={(tags) =>
                                                                            updateTagMetadata(tag.audienceTagId, tags)
                                                                        }
                                                                        initialOptions={tag.options}
                                                                    />
                                                                </Form.Item>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <Popconfirm
                                                                disabled={updating}
                                                                placement="leftBottom"
                                                                title="Delete this tag?"
                                                                onConfirm={() => deleteTag(tag.audienceTagId)}
                                                                cancelButtonProps={{ className: "secondary" }}
                                                            >
                                                                <div style={{ cursor: "pointer" }}>
                                                                    <CloseCircleFilled />
                                                                </div>
                                                            </Popconfirm>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                                <Form.Item>
                                    <IconButton
                                        style={{ marginTop: "10px" }}
                                        type="dashed"
                                        onClick={() => addTag()}
                                        block
                                        icon={<PlusOutlined />}
                                    >
                                        Add tag
                                    </IconButton>
                                </Form.Item>
                            </Form>
                        ) : (
                            <Empty description="No audiences found." />
                        )}
                    </div>
                )}
            </Card>
        </>
    );
};

export default AudiencesTab;
